import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Home, Users, DollarSign, Shield, Bell, FileText, Settings,
  Crosshair, Package, Flag, GraduationCap, Building2, UserPlus, Bug,
  ArrowRight, Command, CornerDownLeft
} from 'lucide-react';

const ACTIONS = [
  { id: 'analytics', label: 'Analytics Dashboard', path: '/admin', icon: Home, group: 'Navigation', keywords: 'home overview metrics stats' },
  { id: 'users', label: 'User Management', path: '/admin/users', icon: Users, group: 'Navigation', keywords: 'manage people accounts' },
  { id: 'leads', label: 'Lead Generation', path: '/admin/leads', icon: Crosshair, group: 'Navigation', keywords: 'prospects marketing' },
  { id: 'revenue', label: 'Revenue & Subscriptions', path: '/admin/revenue', icon: DollarSign, group: 'Navigation', keywords: 'money billing payments subs' },
  { id: 'safety', label: 'Safety & Guardrails', path: '/admin/safety', icon: Shield, group: 'Navigation', keywords: 'moderation content filter' },
  { id: 'audit', label: 'Audit Trail', path: '/admin/audit', icon: FileText, group: 'Navigation', keywords: 'logs history events' },
  { id: 'content', label: 'Content Manager', path: '/admin/content', icon: Package, group: 'Navigation', keywords: 'challenges images ava library' },
  { id: 'features', label: 'Feature Flags', path: '/admin/features', icon: Flag, group: 'Navigation', keywords: 'toggles ab test' },
  { id: 'notifications', label: 'Notification Manager', path: '/admin/notifications', icon: Bell, group: 'Navigation', keywords: 'push alerts messages' },
  { id: 'schools', label: 'Schools', path: '/admin/schools', icon: GraduationCap, group: 'Navigation', keywords: 'education b2b' },
  { id: 'corporates', label: 'Corporates', path: '/admin/corporates', icon: Building2, group: 'Navigation', keywords: 'enterprise workplace b2b' },
  { id: 'reports', label: 'Reports', path: '/admin/reports', icon: FileText, group: 'Navigation', keywords: 'generate download export' },
  { id: 'team', label: 'Team Management', path: '/admin/team', icon: UserPlus, group: 'Navigation', keywords: 'staff admins invite' },
  { id: 'settings', label: 'Admin Settings', path: '/admin/settings', icon: Settings, group: 'Navigation', keywords: 'config preferences' },
  { id: 'dev-support', label: 'Developer Support', path: '/admin/developer-support', icon: Bug, group: 'Navigation', keywords: 'tickets bugs issues' },
  { id: 'go-app', label: 'Switch to User App', path: '/dashboard', icon: ArrowRight, group: 'Quick Actions', keywords: 'user dashboard frontend' },
];

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // ─── Recent items (Phase 4, item 13) ──────────────────────────────────
  // Persist last 5 selected actions to localStorage so they appear as a
  // "Recent" group at the top of the empty palette.
  const RECENT_KEY = 'admin_palette_recent';
  const loadRecent = () => {
    try {
      const ids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return ids
        .map((id) => ACTIONS.find((a) => a.id === id))
        .filter(Boolean)
        .slice(0, 5);
    } catch (_e) { return []; }
  };
  const pushRecent = (id) => {
    try {
      const cur = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const next = [id, ...cur.filter((x) => x !== id)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (_e) {}
  };

  // Memoized so downstream useMemo (flatList) sees a stable reference when
  // the query is empty — otherwise `loadRecent()` returns a fresh array
  // every render, cascading identity flips through the memo chain.
  const recent = useMemo(() => (!query ? loadRecent() : []), [query]);

  const filtered = useMemo(() => ACTIONS.filter(a => {
    if (!query) return true;
    const q = query.toLowerCase();
    return a.label.toLowerCase().includes(q) || a.keywords.includes(q) || a.group.toLowerCase().includes(q);
  }), [query]);

  // Build ordered list: Recent (when empty query) → original groups (de-duped).
  // Memoized so the identity is stable across renders when the query and
  // action set haven't changed — otherwise the useCallback below that
  // depends on it would rebuild every render (linter warning "conditional
  // could make the dependencies change on every render").
  const flatList = useMemo(() => {
    const recentIds = new Set(recent.map((a) => a.id));
    const mainList = filtered.filter((a) => !recentIds.has(a.id));
    return !query ? [...recent, ...mainList] : filtered;
  }, [query, filtered, recent]);

  const grouped = flatList.reduce((acc, item) => {
    const groupKey = recent.includes(item) ? 'Recent' : item.group;
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {});

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback((action) => {
    pushRecent(action.id);
    navigate(action.path);
    onClose();
   
  }, [navigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatList.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatList[selectedIndex]) {
      e.preventDefault();
      handleSelect(flatList[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [flatList, selectedIndex, handleSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" data-testid="command-palette">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search size={18} className="text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search admin actions..."
            className="flex-1 text-sm outline-none placeholder:text-gray-400 bg-transparent"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-72 overflow-y-auto py-2">
          {flatList.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm" data-testid="cmd-empty">
              <p className="text-gray-700 font-semibold mb-1">No matches for &quot;{query}&quot;</p>
              <p className="text-xs text-gray-500">Try a shorter keyword — e.g. &quot;users&quot;, &quot;leads&quot;, &quot;revenue&quot;, &quot;vault&quot;.</p>
            </div>
          ) : (
            Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{group}</p>
                {items.map((action) => {
                  const globalIndex = flatList.indexOf(action);
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      data-index={globalIndex}
                      data-testid={`cmd-action-${action.id}`}
                      onClick={() => handleSelect(action)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        globalIndex === selectedIndex ? 'bg-orange-50 text-orange-800' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} className={globalIndex === selectedIndex ? 'text-orange-500' : 'text-gray-400'} />
                      <span className="flex-1 text-left font-medium">{action.label}</span>
                      {globalIndex === selectedIndex && (
                        <CornerDownLeft size={14} className="text-orange-400" />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><CornerDownLeft size={10} /> Select</span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 border border-gray-300 rounded text-center leading-3 text-[8px]">
              &darr;
            </span>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-200 rounded text-[8px]">ESC</kbd>
            Close
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
